import * as React from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpen,
  CalendarDays,
  Clock,
  ExternalLink,
  GraduationCap,
  Inbox,
  MessageCircle,
  NotebookPen,
  Pencil,
  Plus,
  Presentation,
  RotateCcw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { featureOn } from '@/lib/features'
import { formatDate, localDate } from '@/lib/format'
import { LINK_KIND_LABEL, linkKindOf, linkRow, linkTargetId, sortLinks } from '@/lib/schedule-links'
import type {
  Activity,
  Board,
  Evaluation,
  Game,
  ScheduleEntry,
  ScheduleEntryInternalNote,
  ScheduleEntryKind,
  ScheduleEntryLinkKind,
  ScheduleEntryLinkWithTarget,
  ScheduleEntryStatus,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogDescription,
  DialogFullScreenContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState, ErrorNote, Loading, Markdown, Page } from '@/components/page'
import { EntryLinks, LINK_ICON } from '@/components/entry-links'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

const KIND_META: Record<ScheduleEntryKind, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  topic: { label: 'Topic', icon: Presentation },
  workshop: { label: 'Workshop', icon: GraduationCap },
  reading: { label: 'Reading', icon: BookOpen },
  training: { label: 'Training', icon: Sparkles },
  discussion: { label: 'Discussion', icon: MessageCircle },
  retro: { label: 'Retro', icon: RotateCcw },
  other: { label: 'Other', icon: CalendarDays },
}

const STATUSES: ScheduleEntryStatus[] = ['planned', 'confirmed', 'done', 'skipped']

function statusVariant(status: ScheduleEntryStatus) {
  switch (status) {
    case 'done':
      return 'success' as const
    case 'confirmed':
      return 'outline' as const
    case 'skipped':
      return 'muted' as const
    default:
      return 'muted' as const
  }
}

/** Local midnight today, so "past" is decided by the day and not by the clock. */
function startOfToday(): number {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** "March 2026" — the heading a plan is read under. */
function monthLabel(iso: string): string {
  return localDate(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

export function SchedulePage() {
  const { currentTeam, isLead, isCoach } = useAuth()
  const teamId = currentTeam?.id
  // Games are off for most teams (0046). The links already written stay
  // readable and openable either way — a switch hides a section, it does not
  // withhold data — but there is no sense offering a game to a team that has
  // no Games page to play it on.
  const gamesOn = featureOn(currentTeam, 'games_enabled')
  const [editing, setEditing] = React.useState<ScheduleEntry | 'new' | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId) {
      return {
        entries: [] as ScheduleEntry[],
        topics: [] as Activity[],
        internal: [] as ScheduleEntryInternalNote[],
        links: [] as ScheduleEntryLinkWithTarget[],
      }
    }
    const [entries, topics, internal, links] = await Promise.all([
      sb()
        .from('schedule_entries')
        .select('*')
        .eq('team_id', teamId)
        .order('scheduled_on', { nullsFirst: false })
        .order('display_order')
        .order('created_at'),
      // The whole topic list, because it is both the picker's options and the
      // source of the live title for every linked entry. RLS already hides
      // topics the viewer isn't allowed to see, which is why a linked entry can
      // fall back to its own stored title.
      sb().from('activities').select('*').eq('team_id', teamId).order('display_order').order('created_at'),
      // DPL's notes. The policy in 0036 returns nothing at all to anyone who
      // isn't a coach, so there is no client-side gate to get wrong — a lead's
      // browser never holds a row it would have to remember not to draw.
      sb().from('schedule_entry_internal_notes').select('*').eq('team_id', teamId),
      // What each slot says we will have open (0064). The embeds matter: these
      // rows store no copy of a target's name, so without them every pill
      // would render as "Untitled". One request for the team rather than one
      // per card.
      sb()
        .from('schedule_entry_links')
        .select(
          '*, boards(id, title, archived), evaluations(id, name, status, conducted_on), games(id, slug, title, min_players, active)',
        )
        .eq('team_id', teamId)
        .order('display_order')
        .order('created_at'),
    ])
    if (entries.error) throw entries.error
    if (topics.error) throw topics.error
    if (internal.error) throw internal.error
    if (links.error) throw links.error
    return {
      entries: (entries.data ?? []) as ScheduleEntry[],
      topics: (topics.data ?? []) as Activity[],
      internal: (internal.data ?? []) as ScheduleEntryInternalNote[],
      links: (links.data ?? []) as ScheduleEntryLinkWithTarget[],
    }
  }, [teamId])

  const linksFor = React.useCallback(
    (entryId: string) => sortLinks((data?.links ?? []).filter((l) => l.entry_id === entryId)),
    [data?.links],
  )

  const topicsById = React.useMemo(
    () => new Map((data?.topics ?? []).map((t) => [t.id, t])),
    [data?.topics],
  )

  // Prefer the topic's current title over the copy taken when the entry was
  // filed, so renaming a topic renames its slot. Falls back to the stored title
  // for an entry that names no topic — or whose topic has since been deleted.
  const titleOf = React.useCallback(
    (e: ScheduleEntry) => (e.activity_id ? topicsById.get(e.activity_id)?.title ?? e.title : e.title),
    [topicsById],
  )

  const { upcoming, past, backlog } = React.useMemo(() => {
    const today = startOfToday()
    const up: ScheduleEntry[] = []
    const pa: ScheduleEntry[] = []
    const bl: ScheduleEntry[] = []
    for (const e of data?.entries ?? []) {
      if (!e.scheduled_on) bl.push(e)
      else if (localDate(e.scheduled_on).getTime() < today) pa.push(e)
      else up.push(e)
    }
    // The query sorts oldest-first, which is what a forward plan wants and the
    // wrong way round for history.
    pa.reverse()
    return { upcoming: up, past: pa, backlog: bl }
  }, [data?.entries])

  /** DPL's note for one entry, or undefined. Always empty for a non-coach. */
  function internalFor(entryId: string): ScheduleEntryInternalNote | undefined {
    return (data?.internal ?? []).find((n) => n.entry_id === entryId)
  }

  async function remove(entry: ScheduleEntry) {
    if (!window.confirm(`Remove “${titleOf(entry)}” from the schedule?`)) return
    const { error: err } = await sb().from('schedule_entries').delete().eq('id', entry.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  async function setStatus(entry: ScheduleEntry, status: ScheduleEntryStatus) {
    const { error: err } = await sb().from('schedule_entries').update({ status }).eq('id', entry.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  const row = (e: ScheduleEntry) => {
    const meta = KIND_META[e.kind]
    const topic = e.activity_id ? topicsById.get(e.activity_id) : undefined
    return (
      <Card key={e.id} className={cn(e.status === 'skipped' && 'opacity-60')}>
        <CardContent className="flex items-start gap-3 p-4">
          <div className="mt-0.5 shrink-0 rounded-md bg-muted p-2 text-muted-foreground">
            <meta.icon className="h-4 w-4" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {/* A slot that names a topic opens it — that is the whole point of
                  the link. One that doesn't is plain text, not a dead link. */}
              {topic ? (
                <Link
                  to={`/activities/${topic.id}`}
                  className={cn(
                    'font-medium hover:underline',
                    e.status === 'skipped' && 'line-through decoration-1',
                  )}
                >
                  {topic.title}
                </Link>
              ) : (
                <span className={cn('font-medium', e.status === 'skipped' && 'line-through decoration-1')}>
                  {e.title}
                </span>
              )}
              <Badge variant="muted" className="gap-1 text-[10px]">
                <meta.icon className="h-3 w-3" />
                {meta.label}
              </Badge>
              {e.status !== 'planned' && (
                <Badge variant={statusVariant(e.status)} className="text-[10px]">
                  {e.status}
                </Badge>
              )}
              {/* The entry outlived the topic it pointed at (the FK set the link
                  to null). Say so, rather than letting the stored title read as
                  if the link were still good. */}
              {!e.activity_id && e.kind === 'topic' && (
                <Badge variant="outline" className="text-[10px]">
                  no topic linked
                </Badge>
              )}
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {e.scheduled_on && (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" /> {formatDate(e.scheduled_on)}
                </span>
              )}
              {e.duration_minutes && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {e.duration_minutes} min
                </span>
              )}
              {topic && (
                <Link to={`/activities/${topic.id}`} className="inline-flex items-center gap-1 hover:underline">
                  <ExternalLink className="h-3 w-3" /> Open topic
                </Link>
              )}
            </div>

            {/* Below the metadata rather than above it: the note is the longest
                thing on the card and it reads as a body, not as a subtitle. */}
            <NoteField entry={e} canEdit={isLead} onSaved={refetch} />

            {/* What the team will have open on the day. Read-only here and on
                Today; they are added and removed in the entry editor, because
                that is where the plan is made. */}
            <EntryLinks links={linksFor(e.id)} className="mt-2" />

            {/* Only a coach ever has one of these rows — 0036 returns nothing
                to anyone else — so this draws whatever came back instead of
                re-checking the role. The badge is the reminder that the lead
                sitting next to them cannot see this. */}
            {internalFor(e.id)?.notes && (
              <div className="mt-2 rounded-md border border-dashed border-warning/50 bg-warning/5 px-3 py-2">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs font-semibold">Internal note</span>
                  <Badge
                    variant="outline"
                    className="border-warning/50 text-[10px] font-medium uppercase tracking-wide text-warning"
                  >
                    DPL only
                  </Badge>
                </div>
                <Markdown source={internalFor(e.id)!.notes!} className="text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Planning is a lead action (migrations/0030) — RLS refuses these
              writes from a member, so the controls are absent rather than
              present-and-failing. */}
          {isLead && (
            <div className="flex shrink-0 items-center gap-1.5">
              <Select value={e.status} onValueChange={(v) => void setStatus(e, v as ScheduleEntryStatus)}>
                <SelectTrigger className="h-8 w-[116px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s[0].toUpperCase() + s.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon-sm" onClick={() => setEditing(e)} aria-label="Edit entry">
                <Pencil />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={() => void remove(e)} aria-label="Remove entry">
                <Trash2 />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  // Upcoming is grouped by month; a plan is read a month at a time, and a flat
  // list of twenty dated cards is not a plan you can see the shape of.
  const byMonth = React.useMemo(() => {
    const groups: Array<{ label: string; items: ScheduleEntry[] }> = []
    for (const e of upcoming) {
      const label = monthLabel(e.scheduled_on as string)
      const last = groups[groups.length - 1]
      if (last && last.label === label) last.items.push(e)
      else groups.push({ label, items: [e] })
    }
    return groups
  }, [upcoming])

  return (
    <Page
      title="Schedule"
      actions={
        isLead && (
          <Button onClick={() => setEditing('new')}>
            <Plus /> Schedule something
          </Button>
        )
      }
    >
      {error && <ErrorNote error={error} />}
      {loading && !data ? (
        <Loading />
      ) : (data?.entries.length ?? 0) === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="Nothing scheduled yet"
          description={
            isLead
              ? 'Lay out what the team will cover and when. An entry can point at one of your topics, or stand on its own — a conference, a reading week, outside training.'
              : 'Your team lead or DPL coach hasn’t laid out the plan yet.'
          }
          action={
            isLead && (
              <Button onClick={() => setEditing('new')}>
                <Plus /> Add the first entry
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-6">
          {byMonth.map((g) => (
            <section key={g.label}>
              <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">{g.label}</h2>
              <div className="space-y-2.5">{g.items.map(row)}</div>
            </section>
          ))}

          {backlog.length > 0 && (
            <section>
              <h2 className="mb-2.5 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
                <Inbox className="h-3.5 w-3.5" /> Unscheduled
              </h2>
              <div className="space-y-2.5">{backlog.map(row)}</div>
            </section>
          )}

          {past.length > 0 && (
            <section>
              <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Past</h2>
              <div className="space-y-2.5">{past.map(row)}</div>
            </section>
          )}
        </div>
      )}

      {editing && teamId && (
        <ScheduleEditor
          teamId={teamId}
          topics={data?.topics ?? []}
          entry={editing === 'new' ? undefined : editing}
          // Spaced by 10 so an entry can be slotted between two others later
          // without renumbering the rest — same convention as team goals (0023).
          nextOrder={(data?.entries ?? []).reduce((m, e) => Math.max(m, e.display_order), 0) + 10}
          isCoach={isCoach}
          gamesOn={gamesOn}
          internalNote={editing === 'new' ? undefined : internalFor(editing.id)}
          links={editing === 'new' ? [] : linksFor(editing.id)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

/**
 * The entry's note: rendered Markdown by default, an editor on request.
 *
 * Read-only is the resting state on purpose. A schedule is read far more often
 * than it is written — the whole team opens this page and only the lead edits it
 * — so a page full of textareas would be a page of controls nobody can use. The
 * note only becomes a form when someone asks for it.
 *
 * Editing is its own affordance rather than a trip through the entry dialog:
 * "what came out of this session" gets written after the fact, and reopening a
 * form that also holds the date, kind and status to add a sentence is more
 * ceremony than the thought deserves.
 *
 * Writing is a LEAD action because the whole table is (migrations/0030). RLS is
 * the control; `canEdit` only keeps the button from appearing where it would
 * 403. Note that per-COLUMN write rules aren't expressible in RLS — if members
 * should be able to annotate the plan, that's a child table, not a policy tweak.
 */
function NoteField({
  entry,
  canEdit,
  onSaved,
}: {
  entry: ScheduleEntry
  canEdit: boolean
  onSaved: () => void | Promise<void>
}) {
  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState(entry.notes ?? '')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Compared against the row rather than against an opening snapshot, because
  // this editor is opened and closed repeatedly inside one mounted component.
  const guard = useDiscardGuard(editing && draft !== (entry.notes ?? ''), () => setEditing(false))

  function open() {
    // Re-seed from the row on every open, so a refetch that landed since the
    // last edit isn't overwritten by a stale draft held in this component.
    setDraft(entry.notes ?? '')
    setError(null)
    setEditing(true)
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      // Empty clears the note rather than storing "", so `notes && …` stays the
      // one test for "is there a note" everywhere else.
      const { error: err } = await sb()
        .from('schedule_entries')
        .update({ notes: draft.trim() || null })
        .eq('id', entry.id)
      if (err) throw err
      setEditing(false)
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (!editing) {
    if (!entry.notes) {
      // Nothing to read and no way to write it — render nothing at all rather
      // than an empty-state line on every undocumented row.
      if (!canEdit) return null
      return (
        <button
          type="button"
          onClick={open}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <NotebookPen className="h-3.5 w-3.5" /> Add a note
        </button>
      )
    }
    return (
      <div className="group/note mt-2 rounded-md border bg-muted/30 px-3 py-2">
        <Markdown source={entry.notes} className="text-muted-foreground" />
        {canEdit && (
          <button
            type="button"
            onClick={open}
            // Always reachable by keyboard; revealed on hover for the mouse, so
            // a read-only page isn't peppered with pencils.
            className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/note:opacity-100"
          >
            <Pencil className="h-3 w-3" /> Edit note
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="mt-2 space-y-2">
      <DiscardPrompt guard={guard} what="this note" />
      <Textarea
        autoFocus
        rows={5}
        value={draft}
        onChange={(ev) => setDraft(ev.target.value)}
        // Mono, because what's being typed is Markdown source rather than the
        // prose it renders to.
        className="font-mono text-xs"
        placeholder="What to prepare, why now, what came out of it. Markdown."
        onKeyDown={(ev) => {
          // Escape abandons, Cmd/Ctrl+Enter saves. A note gets written mid-meeting
          // and reaching for the mouse to keep a thought is how the thought is lost.
          if (ev.key === 'Escape') {
            ev.preventDefault()
            guard.requestClose()
          } else if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) {
            ev.preventDefault()
            void save()
          }
        }}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" onClick={() => void save()} disabled={busy}>
          Save note
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={guard.requestClose} disabled={busy}>
          Cancel
        </Button>
        <span className="text-xs text-muted-foreground">
          Markdown · <kbd className="font-sans">⌘↵</kbd> to save, <kbd className="font-sans">Esc</kbd> to cancel
        </span>
      </div>
    </div>
  )
}

/** The sentinel for "no topic" — Radix Select rejects an empty-string value. */
const NO_TOPIC = '__none__'

/**
 * A link as the editor holds it: either an existing row (`key` is its id) or
 * one added in this dialog and not yet written (`key` starts with `new:`).
 *
 * The title is carried along rather than looked up at render time because the
 * two sources differ — an existing row brings its embed, a newly picked one
 * comes from the options list — and the pill should not care which.
 */
interface StagedLink {
  key: string
  kind: ScheduleEntryLinkKind
  targetId: string
  title: string
}

function stageExisting(link: ScheduleEntryLinkWithTarget): StagedLink {
  return {
    key: link.id,
    kind: linkKindOf(link) ?? 'board',
    targetId: linkTargetId(link) ?? '',
    title: link.boards?.title ?? link.evaluations?.name ?? link.games?.title ?? 'Untitled',
  }
}

function ScheduleEditor({
  teamId,
  topics,
  entry,
  nextOrder,
  isCoach,
  gamesOn,
  internalNote,
  links,
  onClose,
  onSaved,
}: {
  teamId: string
  topics: Activity[]
  entry?: ScheduleEntry
  nextOrder: number
  /** Whether to draw the internal pane. The database decides who may write one. */
  isCoach: boolean
  /** Whether to offer games at all. See the note where this is computed. */
  gamesOn: boolean
  /** DPL's existing note for this entry, if there is one and you can see it. */
  internalNote?: ScheduleEntryInternalNote
  /** What this slot already says we will have open. Empty for a new entry. */
  links: ScheduleEntryLinkWithTarget[]
  onClose: () => void
  onSaved: () => void
}) {
  const [activityId, setActivityId] = React.useState(entry?.activity_id ?? NO_TOPIC)
  const [title, setTitle] = React.useState(entry?.title ?? '')
  const [kind, setKind] = React.useState<ScheduleEntryKind>(entry?.kind ?? 'topic')
  const [scheduledOn, setScheduledOn] = React.useState(entry?.scheduled_on ?? '')
  const [duration, setDuration] = React.useState(entry?.duration_minutes?.toString() ?? '')
  const [status, setStatus] = React.useState<ScheduleEntryStatus>(entry?.status ?? 'planned')
  const [notes, setNotes] = React.useState(entry?.notes ?? '')
  const [internal, setInternal] = React.useState(internalNote?.notes ?? '')
  // The links being planned, kept locally until the entry is saved — a new
  // entry has no id to hang them off yet, exactly like the internal note
  // below. `key` is the existing row's id, or `new:…` for one added here; that
  // is what the save diffs on.
  const [staged, setStaged] = React.useState<StagedLink[]>(() => links.map(stageExisting))
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({
    activityId,
    title,
    kind,
    scheduledOn,
    duration,
    status,
    notes,
    internal,
    // Keys, not objects: the targets carry titles and flags that a refetch can
    // change under the dialog, and a changed title is not an unsaved edit.
    links: staged.map((l) => l.key),
  })
  const guard = useDiscardGuard(dirty, onClose)

  const linked = activityId !== NO_TOPIC

  // The three catalogs a link can point at, read only while this dialog is
  // open — the Schedule page itself has no use for them, and a team with
  // thirty boards should not be paying for that list on every page load.
  const { data: options } = useQuery(async () => {
    const none = { boards: [] as Board[], evaluations: [] as Evaluation[], games: [] as Game[] }
    if (!teamId) return none
    const [boards, evaluations, games] = await Promise.all([
      // Archived boards are left out of the PICKER and stay readable as links
      // that already exist: a board archived after Tuesday was planned is
      // still what Tuesday meant.
      sb()
        .from('boards')
        .select('*')
        .eq('team_id', teamId)
        .eq('archived', false)
        .order('updated_at', { ascending: false }),
      sb()
        .from('evaluations')
        .select('*')
        .eq('team_id', teamId)
        .order('conducted_on', { ascending: false }),
      gamesOn
        ? sb().from('games').select('*').eq('active', true).order('display_order')
        : Promise.resolve({ data: [], error: null }),
    ])
    if (boards.error) throw boards.error
    if (evaluations.error) throw evaluations.error
    if (games.error) throw games.error
    return {
      boards: (boards.data ?? []) as Board[],
      evaluations: (evaluations.data ?? []) as Evaluation[],
      games: (games.data ?? []) as Game[],
    }
  }, [teamId, gamesOn])

  function addLink(kind: ScheduleEntryLinkKind, targetId: string, title: string) {
    setStaged((prev) =>
      // Guard the click, not the database: a partial unique index refuses the
      // duplicate anyway (0064), and meeting it as a 409 after filling in the
      // rest of the form is a poor way to learn the board is already on.
      prev.some((l) => l.kind === kind && l.targetId === targetId)
        ? prev
        : [...prev, { key: `new:${kind}:${targetId}`, kind, targetId, title }],
    )
  }

  function removeLink(key: string) {
    setStaged((prev) => prev.filter((l) => l.key !== key))
  }

  // Picking a topic fills the title and the kind from it, so the common case is
  // two clicks and a date. Typed-over values are kept: only a blank title is
  // replaced, and the kind only moves TO 'topic', never away from a choice the
  // lead made deliberately.
  function pickTopic(id: string) {
    setActivityId(id)
    if (id === NO_TOPIC) return
    const t = topics.find((x) => x.id === id)
    if (t && !title.trim()) setTitle(t.title)
    setKind('topic')
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!linked && !title.trim()) return setError('Pick a topic, or give the entry a title.')
    const minutes = duration.trim() ? Number(duration) : null
    if (minutes !== null && (!Number.isFinite(minutes) || minutes <= 0)) {
      return setError('Duration has to be a positive number of minutes.')
    }

    setBusy(true)
    setError(null)
    try {
      const row = {
        team_id: teamId,
        activity_id: linked ? activityId : null,
        // Blank is allowed here only because a topic is linked — the trigger in
        // 0030 backfills the topic's title, which is also what keeps the row
        // readable if that topic is later deleted.
        title: title.trim() || null,
        kind,
        scheduled_on: scheduledOn || null,
        duration_minutes: minutes,
        status,
        notes: notes.trim() || null,
        ...(entry ? {} : { display_order: nextOrder }),
      }
      let entryId = entry?.id
      if (entry) {
        const { error: err } = await sb().from('schedule_entries').update(row).eq('id', entry.id)
        if (err) throw err
      } else {
        const { data: created, error: err } = await sb()
          .from('schedule_entries')
          .insert(row)
          .select('id')
          .single()
        if (err) throw err
        entryId = (created as { id: string }).id
      }

      // The links, diffed against what was there when the dialog opened. Both
      // halves are one request: the removals go as a single `in` delete and
      // the additions as a single bulk insert, the same shape the board's
      // group actions use — a partial failure here would leave a plan that
      // disagrees with the dialog the lead is looking at.
      if (entryId) {
        const removed = links.filter((l) => !staged.some((st) => st.key === l.id)).map((l) => l.id)
        if (removed.length) {
          const { error: err } = await sb().from('schedule_entry_links').delete().in('id', removed)
          if (err) throw err
        }
        const added = staged
          .map((st, i) => ({ st, i }))
          .filter(({ st }) => st.key.startsWith('new:'))
        if (added.length) {
          const { error: err } = await sb()
            .from('schedule_entry_links')
            // Spaced by 10 by position, so a link added between two others
            // later has somewhere to go — same convention as the entries.
            .insert(added.map(({ st, i }) => linkRow(entryId, st.kind, st.targetId, (i + 1) * 10)))
          if (err) throw err
        }
      }

      // Its own row with its own audience, so its own write — attempted only
      // when there is something to say or something to clear. A lead never gets
      // here: the field isn't drawn for them, and 0036 would refuse it anyway.
      if (isCoach && entryId && (internal.trim() || internalNote)) {
        const body = internal.trim()
        const { error: err } = body
          ? await sb()
              .from('schedule_entry_internal_notes')
              .upsert({ entry_id: entryId, team_id: teamId, notes: body }, { onConflict: 'entry_id' })
          : // Emptied on purpose: drop the row rather than keep a blank one, so
            // `notes && …` stays the one test for "is there an internal note".
            await sb().from('schedule_entry_internal_notes').delete().eq('entry_id', entryId)
        if (err) throw err
      }

      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      {/* Full screen, matching the meeting editor: an entry now carries two
          notes, and both are prose. A 'xl' panel gave them three rows each and
          the plan's reasoning got written somewhere else instead. */}
      <DialogFullScreenContent>
        <DiscardPrompt guard={guard} what="this schedule entry" />
        <form onSubmit={save} className="flex h-full flex-col">
          {/* Pinned header — with two note fields the form outgrows the window,
              and a Save you have to scroll to is a Save people stop trusting. */}
          <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">
                {entry ? 'Edit schedule entry' : 'Schedule something'}
              </DialogTitle>
              <DialogDescription className="truncate text-xs">
                Link one of your topics, or describe something that isn’t one — a conference,
                outside training, a reading week.
              </DialogDescription>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button type="button" variant="ghost" onClick={guard.requestClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {entry ? 'Save changes' : 'Add to schedule'}
              </Button>
              <Button type="button" variant="ghost" size="icon-sm" onClick={guard.requestClose} aria-label="Close">
                <X />
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            {/* The slot on the left, the writing on the right. One column below
                lg, in the order you fill them. */}
            <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="stopic">Topic</Label>
                  <Select value={activityId} onValueChange={pickTopic}>
                    <SelectTrigger id="stopic">
                      <SelectValue placeholder="No topic — I’ll describe it" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[50vh]">
                      <SelectItem value={NO_TOPIC}>No topic — I’ll describe it</SelectItem>
                      {topics.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stitle">Title</Label>
                  <Input
                    id="stitle"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={linked ? 'Defaults to the topic’s title' : 'What are we doing?'}
                  />
                  {linked && (
                    <p className="text-xs text-muted-foreground">
                      Leave this blank to follow the topic — renaming the topic renames the slot.
                    </p>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="sdate">Date</Label>
                    <Input
                      id="sdate"
                      type="date"
                      value={scheduledOn}
                      onChange={(e) => setScheduledOn(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Blank keeps it unscheduled.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sdur">Minutes</Label>
                    <Input
                      id="sdur"
                      type="number"
                      min={1}
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      placeholder="60"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sstatus">Status</Label>
                    <Select value={status} onValueChange={(v) => setStatus(v as ScheduleEntryStatus)}>
                      <SelectTrigger id="sstatus">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s[0].toUpperCase() + s.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="skind">Kind</Label>
                  <Select value={kind} onValueChange={(v) => setKind(v as ScheduleEntryKind)}>
                    <SelectTrigger id="skind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(KIND_META) as ScheduleEntryKind[]).map((k) => (
                        <SelectItem key={k} value={k}>
                          {KIND_META[k].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <LinkPicker
                  staged={staged}
                  boards={options?.boards ?? []}
                  evaluations={options?.evaluations ?? []}
                  games={options?.games ?? []}
                  onAdd={addLink}
                  onRemove={removeLink}
                />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="snotes">Notes</Label>
                  <Textarea
                    id="snotes"
                    rows={10}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Why now, what to prepare, who’s leading it. Markdown."
                  />
                  <p className="text-xs text-muted-foreground">Everyone on the team can read these.</p>
                </div>

                {isCoach && (
                  <div className="space-y-2 rounded-md border border-dashed border-warning/50 bg-warning/5 p-3">
                    <Label htmlFor="sinternal" className="flex items-center gap-2">
                      Internal notes
                      <Badge
                        variant="outline"
                        className="border-warning/50 text-[10px] font-medium uppercase tracking-wide text-warning"
                      >
                        DPL only
                      </Badge>
                    </Label>
                    <Textarea
                      id="sinternal"
                      rows={8}
                      value={internal}
                      onChange={(e) => setInternal(e.target.value)}
                      placeholder="Why this is really on the plan. What to watch for. What to move if it slips."
                    />
                    <p className="text-xs text-muted-foreground">
                      Visible to DPL coaches and admins only — not to the team, and not to the team lead.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {error && <p className="mx-auto mt-4 max-w-6xl text-sm text-destructive">{error}</p>}
          </div>
        </form>
      </DialogFullScreenContent>
    </Dialog>
  )
}

/**
 * "What we'll have open" — the links on a slot (0064).
 *
 * A list of buttons rather than three dropdowns, which is the shape this
 * started as. A dropdown that adds something is a control with no resting
 * value: it has to be reset after every pick, it reads as "which board is this
 * slot" rather than "add a board", and Radix refuses the empty value that
 * would make it look right. Buttons also make the common case — the team has
 * four boards and wants two of them — one click each.
 *
 * Only what can still be added is offered: anything already on the slot is a
 * pill above instead, which is also what keeps the lead from meeting the
 * partial unique index as a 409.
 */
function LinkPicker({
  staged,
  boards,
  evaluations,
  games,
  onAdd,
  onRemove,
}: {
  staged: StagedLink[]
  boards: Board[]
  evaluations: Evaluation[]
  games: Game[]
  onAdd: (kind: ScheduleEntryLinkKind, targetId: string, title: string) => void
  onRemove: (key: string) => void
}) {
  const taken = (kind: ScheduleEntryLinkKind) =>
    new Set(staged.filter((l) => l.kind === kind).map((l) => l.targetId))

  const groups: Array<{
    kind: ScheduleEntryLinkKind
    items: Array<{ id: string; title: string; hint?: string }>
  }> = [
    {
      kind: 'board',
      items: boards.map((b) => ({ id: b.id, title: b.title })),
    },
    {
      kind: 'evaluation',
      items: evaluations.map((e) => ({
        id: e.id,
        title: e.name,
        // Two evaluations of the same template are told apart by their date,
        // which is also the column the Evaluations page labels them by.
        hint: formatDate(e.conducted_on),
      })),
    },
    { kind: 'game', items: games.map((g) => ({ id: g.id, title: g.title })) },
  ]

  const offered = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !taken(g.kind).has(i.id)) }))
    .filter((g) => g.items.length > 0)

  return (
    <div className="space-y-2">
      <Label>What we’ll have open</Label>
      <p className="text-xs text-muted-foreground">
        The boards, evaluations and games the team will use on the day. They show on the card here
        and, on the day itself, on Today.
      </p>

      {staged.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {staged.map((l) => {
            const Icon = LINK_ICON[l.kind]
            return (
              <span
                key={l.key}
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-1 text-xs"
              >
                <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{l.title}</span>
                <button
                  type="button"
                  onClick={() => onRemove(l.key)}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={`Remove ${l.title}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )
          })}
        </div>
      )}

      {offered.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {staged.length > 0
            ? 'Everything there is to link is already on this slot.'
            : 'Nothing to link yet — make a board or start an evaluation first.'}
        </p>
      ) : (
        /* Two columns, not three. Three fits the three kinds and gives each
           about 165px, which truncates "Process Assessment — current" to
           "Process A…" — a picker you cannot read is not a picker. */
        <div className="grid gap-2 sm:grid-cols-2">
          {offered.map((g) => {
            const Icon = LINK_ICON[g.kind]
            return (
              <div key={g.kind} className="rounded-md border p-2">
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Icon className="h-3 w-3" /> {LINK_KIND_LABEL[g.kind]}
                </div>
                {/* Capped and scrollable: a team two years in has a lot of
                    boards, and this sits inside a form that is already tall. */}
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {g.items.map((i) => (
                    /* `title` carries the full name: the row is one line and
                       some of these are long. */
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => onAdd(g.kind, i.id, i.title)}
                      title={i.title}
                      className="flex w-full items-start gap-1.5 rounded px-1.5 py-1 text-left text-xs hover:bg-muted"
                    >
                      <Plus className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="min-w-0">
                        <span className="block truncate">{i.title}</span>
                        {/* Under the name rather than beside it: two
                            evaluations of the same template are told apart by
                            their date, so it must not be the thing that gets
                            squeezed out. */}
                        {i.hint && <span className="block text-[10px] text-muted-foreground">{i.hint}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
